/**
 * VRG-761 — the counts the delete-package confirmation needs, read FROM THE SERVER.
 *
 *   GET /packages/:id/delete-preview  ->  { tagsTotal, tagsUnused, tagsUsed }
 *
 * ⚠️ DO NOT compute "unused" on the client. The server applies `UNUSED_TAG_CONDITION`
 * (apps/api/src/database/tag-unused.ts) and the delete path applies the SAME fragment, so the
 * number this modal shows is the number of rows the button will actually remove. A client-side
 * reimplementation would be a second, independent answer to one question, and the modal would
 * eventually promise something the server does not do — which is the whole defect this ticket is
 * about, one layer up.
 *
 * `staleTime: 0` on purpose: the count is read at the moment someone is about to destroy
 * something, so a cached answer from five minutes ago is the wrong kind of fast.
 */
import { useQuery } from '@tanstack/react-query'
import { createApiCall } from 'services'

export type PackageDeletePreview = {
  packageId: string
  /** Every tag currently in the box. */
  tagsTotal: number
  /** Tags with no trace of use — never encoded, no certificate, no scans, never moved. */
  tagsUnused: number
  /** Tags that have been used. These are ALWAYS kept, detached from the box. */
  tagsUsed: number
}

export const usePackageDeletePreview = (
  packageId: string | null | undefined,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ['packages', 'delete-preview', packageId],
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: `/packages/${packageId}/delete-preview`,
      })
      return response.data as PackageDeletePreview
    },
    enabled: Boolean(packageId) && options?.enabled !== false,
    staleTime: 0,
    gcTime: 0,
  })
}
